import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Sparkles } from 'lucide-react';
import { neonColors, sizes, backboardColors, backboardStyles, backgrounds } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';

// Real Google Fonts for neon signs
const neonFonts = [
  { id: 'pacifico', name: 'Pacifico', family: "'Pacifico', cursive", popular: true },
  { id: 'great-vibes', name: 'Great Vibes', family: "'Great Vibes', cursive", popular: true },
  { id: 'sacramento', name: 'Sacramento', family: "'Sacramento', cursive" },
  { id: 'dancing-script', name: 'Dancing Script', family: "'Dancing Script', cursive" },
  { id: 'allura', name: 'Allura', family: "'Allura', cursive" },
  { id: 'alex-brush', name: 'Alex Brush', family: "'Alex Brush', cursive" },
  { id: 'satisfy', name: 'Satisfy', family: "'Satisfy', cursive" },
  { id: 'kaushan-script', name: 'Kaushan Script', family: "'Kaushan Script', cursive" },
  { id: 'permanent-marker', name: 'Permanent Marker', family: "'Permanent Marker', cursive", popular: true },
  { id: 'yellowtail', name: 'Yellowtail', family: "'Yellowtail', cursive" },
  { id: 'lobster', name: 'Lobster', family: "'Lobster', cursive" },
  { id: 'cookie', name: 'Cookie', family: "'Cookie', cursive" },
  { id: 'caveat', name: 'Caveat', family: "'Caveat', cursive" },
  { id: 'indie-flower', name: 'Indie Flower', family: "'Indie Flower', cursive" },
  { id: 'shadows-into-light', name: 'Shadows Into Light', family: "'Shadows Into Light', cursive" },
  { id: 'amatic-sc', name: 'Amatic SC', family: "'Amatic SC', cursive" },
  { id: 'courgette', name: 'Courgette', family: "'Courgette', cursive" },
  { id: 'gloria-hallelujah', name: 'Gloria Hallelujah', family: "'Gloria Hallelujah', cursive" },
  { id: 'architects-daughter', name: 'Architects Daughter', family: "'Architects Daughter', cursive" },
  { id: 'homemade-apple', name: 'Homemade Apple', family: "'Homemade Apple', cursive" },
];

const NeonCustomizer = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [customText, setCustomText] = useState('Your Text');
  const [selectedFont, setSelectedFont] = useState(neonFonts[0]);
  const [selectedColor, setSelectedColor] = useState(neonColors[9]); // Pink
  const [selectedSize, setSelectedSize] = useState(sizes[3]); // Medium
  const [selectedBackboard, setSelectedBackboard] = useState(backboardColors[0]);
  const [selectedStyle, setSelectedStyle] = useState(backboardStyles[0]);
  const [selectedBackground, setSelectedBackground] = useState(backgrounds[0]);
  const [isIndoor, setIsIndoor] = useState(true);

  // Load Google Fonts
  useEffect(() => {
    const fontFamilies = neonFonts.map(f => f.name.replace(/ /g, '+')).join('&family=');
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamilies}&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  return (
    <div 
      className="w-full py-16 px-4 transition-colors duration-300"
      style={{
        background: isDark 
          ? 'linear-gradient(to bottom, #000000 0%, #0a0a0a 50%, #000000 100%)'
          : 'linear-gradient(to bottom, #f8fafc 0%, #ffffff 50%, #f8fafc 100%)'
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 
            className="text-4xl md:text-5xl font-bold mb-4 transition-colors duration-300"
            style={{ 
              color: isDark ? '#00ffff' : '#0f172a',
              textShadow: isDark ? '0 0 30px rgba(0, 255, 255, 0.8), 0 0 50px rgba(0, 255, 255, 0.5)' : 'none'
            }}
          >
            {t('neonCustomizer.title') || 'Create Your Custom Neon Sign'}
          </h2>
          <p 
            className="text-lg transition-colors duration-300"
            style={{ 
              color: isDark ? '#ff00ff' : '#64748b',
              textShadow: isDark ? '0 0 10px rgba(255, 0, 255, 0.6)' : 'none'
            }}
          >
            {t('neonCustomizer.subtitle') || 'Design your perfect sign in real-time with our interactive customizer'}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Preview Section */}
          <div className="space-y-4">
            <Card 
              className="p-4 transition-colors duration-300"
              style={{
                backgroundColor: isDark ? '#0a0a0a' : '#ffffff',
                borderColor: isDark ? '#ff00ff' : '#e2e8f0',
                borderWidth: isDark ? '2px' : '1px',
                boxShadow: isDark ? '0 0 30px rgba(255, 0, 255, 0.2)' : 'none'
              }}
            >
              <div className="relative aspect-video rounded-lg overflow-hidden">
                <img 
                  src={selectedBackground.image} 
                  alt="Background"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center p-4">
                  <div 
                    className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold px-4 sm:px-8 py-2 sm:py-4 transition-all duration-300 text-center break-words max-w-full"
                    style={{
                      fontFamily: selectedFont.family,
                      color: selectedColor.hex,
                      textShadow: `0 0 10px ${selectedColor.hex}, 0 0 20px ${selectedColor.glow}, 0 0 40px ${selectedColor.glow}`,
                      wordBreak: 'break-word',
                    }}
                  >
                    {customText || 'Your Text'}
                  </div>
                </div>
              </div>
              
              <div className="mt-4">
                <Label 
                  className="mb-2 block transition-colors duration-300"
                  style={{ color: isDark ? '#00ffff' : '#0f172a' }}
                >
                  Preview Backgrounds
                </Label>
                <div className="grid grid-cols-4 gap-2">
                  {backgrounds.map((bg) => (
                    <button
                      key={bg.id}
                      onClick={() => setSelectedBackground(bg)}
                      className="relative aspect-video rounded overflow-hidden border-2 transition-all hover:scale-105"
                      style={{
                        borderColor: selectedBackground.id === bg.id ? (isDark ? '#00ffff' : '#3b82f6') : (isDark ? '#333' : '#cbd5e1')
                      }}
                    >
                      <img src={bg.image} alt={bg.name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                        <span className="text-xs text-white font-medium">{bg.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Get Quote Button */}
              <div className="mt-4">
                <Button 
                  className="w-full text-white font-bold py-6 text-lg"
                  onClick={() => {
                    const quoteSection = document.getElementById('quote');
                    if (quoteSection) {
                      quoteSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  style={{
                    background: isDark 
                      ? 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)'
                      : 'linear-gradient(135deg, #d946ef 0%, #0ea5e9 100%)',
                    boxShadow: isDark 
                      ? '0 10px 40px rgba(255, 0, 255, 0.4)'
                      : '0 10px 40px rgba(217, 70, 239, 0.4)'
                  }}
                >
                  {t('neonCustomizer.getQuote') || 'Get Your Quote'}
                </Button>
              </div>
            </Card>
          </div>

          {/* Customization Options */}
          <div>
            <Card 
              className="transition-colors duration-300"
              style={{
                backgroundColor: isDark ? '#0a0a0a' : '#ffffff',
                borderColor: isDark ? '#00ffff' : '#e2e8f0',
                borderWidth: isDark ? '2px' : '1px'
              }}
            >
              <Tabs defaultValue="text" className="w-full">
                <TabsList 
                  className="grid w-full grid-cols-5 transition-colors duration-300"
                  style={{ backgroundColor: isDark ? '#000000' : '#f1f5f9' }}
                >
                  <TabsTrigger value="text">Text</TabsTrigger>
                  <TabsTrigger value="font">Font</TabsTrigger>
                  <TabsTrigger value="color">Color</TabsTrigger>
                  <TabsTrigger value="size">Size</TabsTrigger>
                  <TabsTrigger value="options">Options</TabsTrigger>
                </TabsList>

                {/* Text Tab */}
                <TabsContent value="text" className="p-6 space-y-4">
                  <div>
                    <Label 
                      className="mb-2 block transition-colors duration-300"
                      style={{ color: isDark ? '#00ffff' : '#0f172a' }}
                    >
                      Enter Your Text
                    </Label>
                    <Input
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      placeholder="Type your text here..."
                      className="text-lg transition-colors duration-300"
                      style={{
                        backgroundColor: isDark ? '#000000' : '#f8fafc',
                        borderColor: isDark ? '#ff00ff' : '#cbd5e1',
                        color: isDark ? '#ffffff' : '#0f172a'
                      }}
                      maxLength={50}
                    />
                    <p 
                      className="text-xs mt-1 transition-colors duration-300"
                      style={{ color: isDark ? '#888' : '#94a3b8' }}
                    >
                      {customText.length}/50 characters
                    </p>
                  </div>
                </TabsContent>

                {/* Font Tab */}
                <TabsContent value="font" className="p-6">
                  <ScrollArea className="h-[400px]">
                    <div className="grid grid-cols-2 gap-3">
                      {neonFonts.map((font) => (
                        <button
                          key={font.id}
                          onClick={() => setSelectedFont(font)}
                          className="relative p-4 rounded-lg border-2 transition-all hover:scale-102 text-left"
                          style={{
                            borderColor: selectedFont.id === font.id 
                              ? (isDark ? '#00ffff' : '#3b82f6') 
                              : (isDark ? '#333' : '#cbd5e1'),
                            backgroundColor: selectedFont.id === font.id 
                              ? (isDark ? 'rgba(0, 255, 255, 0.1)' : 'rgba(59, 130, 246, 0.1)') 
                              : 'transparent'
                          }}
                        >
                          <p 
                            className="text-xl mb-1 truncate"
                            style={{ 
                              fontFamily: font.family,
                              color: isDark ? '#ffffff' : '#0f172a'
                            }}
                          >
                            {customText || 'Preview'}
                          </p>
                          <p 
                            className="text-xs"
                            style={{ color: isDark ? '#888' : '#64748b' }}
                          >
                            {font.name}
                          </p>
                          {font.popular && (
                            <Badge 
                              className="absolute -top-2 -right-2 text-xs border-0"
                              style={{
                                background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)'
                              }}
                            >
                              <Sparkles className="w-3 h-3" />
                            </Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Color Tab */}
                <TabsContent value="color" className="p-6">
                  <ScrollArea className="h-[400px]">
                    <div className="grid grid-cols-4 gap-3">
                      {neonColors.map((color) => (
                        <button
                          key={color.id}
                          onClick={() => setSelectedColor(color)}
                          className="relative p-3 rounded-lg border-2 transition-all hover:scale-105 group"
                          style={{
                            borderColor: selectedColor.id === color.id ? (isDark ? '#00ffff' : '#3b82f6') : (isDark ? '#333' : '#cbd5e1')
                          }}
                        >
                          <div 
                            className="w-full h-16 rounded mb-2 transition-all group-hover:scale-110"
                            style={{ 
                              backgroundColor: color.hex,
                              boxShadow: `0 0 15px ${color.glow}, 0 0 30px ${color.glow}`
                            }}
                          />
                          <p 
                            className="text-xs text-center font-medium"
                            style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                          >
                            {color.name}
                          </p>
                          {color.popular && (
                            <Badge 
                              className="absolute -top-2 -right-2 text-xs border-0"
                              style={{
                                background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)'
                              }}
                            >
                              <Sparkles className="w-3 h-3" />
                            </Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Size Tab */}
                <TabsContent value="size" className="p-6">
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-3">
                      {sizes.map((size) => (
                        <button
                          key={size.id}
                          onClick={() => setSelectedSize(size)}
                          className="w-full p-4 rounded-lg border-2 transition-all hover:scale-102 text-left"
                          style={{
                            borderColor: selectedSize.id === size.id 
                              ? (isDark ? '#00ffff' : '#3b82f6') 
                              : (isDark ? '#333' : '#cbd5e1'),
                            backgroundColor: selectedSize.id === size.id 
                              ? (isDark ? 'rgba(0, 255, 255, 0.1)' : 'rgba(59, 130, 246, 0.1)') 
                              : 'transparent'
                          }}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p 
                                className="font-bold"
                                style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                              >
                                {size.name}
                              </p>
                              <p 
                                className="text-sm"
                                style={{ color: isDark ? '#888' : '#64748b' }}
                              >
                                {size.widthInch}" ({size.width}cm) wide
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Options Tab */}
                <TabsContent value="options" className="p-6 space-y-6">
                  {/* Backboard Color */}
                  <div>
                    <Label 
                      className="mb-3 block"
                      style={{ color: isDark ? '#00ffff' : '#0f172a' }}
                    >
                      Backboard Color
                    </Label>
                    <div className="grid grid-cols-5 gap-2">
                      {backboardColors.map((board) => (
                        <button
                          key={board.id}
                          onClick={() => setSelectedBackboard(board)}
                          className="p-2 rounded-lg border-2 transition-all hover:scale-105 text-center"
                          style={{
                            borderColor: selectedBackboard.id === board.id 
                              ? (isDark ? '#00ffff' : '#3b82f6') 
                              : (isDark ? '#333' : '#cbd5e1')
                          }}
                        >
                          <div 
                            className="w-full h-8 rounded mb-1 mx-auto"
                            style={{ 
                              backgroundColor: board.image === 'transparent' ? 'transparent' : board.image,
                              border: board.image === 'transparent' ? '2px dashed #666' : 'none'
                            }}
                          />
                          <p 
                            className="text-xs truncate"
                            style={{ color: isDark ? '#888' : '#64748b' }}
                          >
                            {board.name}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Backboard Style */}
                  <div>
                    <Label 
                      className="mb-3 block"
                      style={{ color: isDark ? '#00ffff' : '#0f172a' }}
                    >
                      Backboard Style
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {backboardStyles.map((style) => (
                        <button
                          key={style.id}
                          onClick={() => setSelectedStyle(style)}
                          className="p-3 rounded-lg border-2 transition-all hover:scale-102 text-left"
                          style={{
                            borderColor: selectedStyle.id === style.id 
                              ? (isDark ? '#00ffff' : '#3b82f6') 
                              : (isDark ? '#333' : '#cbd5e1'),
                            backgroundColor: selectedStyle.id === style.id 
                              ? (isDark ? 'rgba(0, 255, 255, 0.1)' : 'rgba(59, 130, 246, 0.1)') 
                              : 'transparent'
                          }}
                        >
                          <p 
                            className="font-medium text-sm"
                            style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                          >
                            {style.name}
                          </p>
                          <p 
                            className="text-xs mt-1"
                            style={{ color: isDark ? '#666' : '#94a3b8' }}
                          >
                            {style.description}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Indoor/Outdoor Toggle */}
                  <div>
                    <Label 
                      className="mb-3 block"
                      style={{ color: isDark ? '#00ffff' : '#0f172a' }}
                    >
                      Location
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setIsIndoor(true)}
                        className="p-4 rounded-lg border-2 transition-all"
                        style={{
                          borderColor: isIndoor 
                            ? (isDark ? '#00ffff' : '#3b82f6') 
                            : (isDark ? '#333' : '#cbd5e1'),
                          backgroundColor: isIndoor 
                            ? (isDark ? 'rgba(0, 255, 255, 0.1)' : 'rgba(59, 130, 246, 0.1)') 
                            : 'transparent'
                        }}
                      >
                        <p 
                          className="font-medium"
                          style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                        >
                          Indoor
                        </p>
                        <p 
                          className="text-xs"
                          style={{ color: isDark ? '#888' : '#64748b' }}
                        >
                          Standard protection
                        </p>
                      </button>
                      <button
                        onClick={() => setIsIndoor(false)}
                        className="p-4 rounded-lg border-2 transition-all"
                        style={{
                          borderColor: !isIndoor 
                            ? (isDark ? '#00ffff' : '#3b82f6') 
                            : (isDark ? '#333' : '#cbd5e1'),
                          backgroundColor: !isIndoor 
                            ? (isDark ? 'rgba(0, 255, 255, 0.1)' : 'rgba(59, 130, 246, 0.1)') 
                            : 'transparent'
                        }}
                      >
                        <p 
                          className="font-medium"
                          style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                        >
                          Outdoor
                        </p>
                        <p 
                          className="text-xs"
                          style={{ color: isDark ? '#888' : '#64748b' }}
                        >
                          IP67 Waterproof
                        </p>
                      </button>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NeonCustomizer;
